import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faQuestionCircle,
  faCheckCircle,
  faTimesCircle,
  faClock,
  faSpinner,
  faPlus,
  faEye,
  faTrash,
  faExclamationTriangle,
  faRobot,
  faUser,
  faQuoteLeft,
  faExternalLinkAlt,
  faCopy,
  faChevronDown,
  faChevronUp,
  faInfoCircle,
  faPlay,
  faStream
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import FeedbackButtons from '../../components/chat/FeedbackButtons';
import { feedbackService } from '../../services/feedbackService';
import MessageRenderer from '../../components/chat/MessageRenderer';
import CitationTooltip from '../../components/chat/CitationTooltip';
import { useCitationTooltip } from '../../components/chat/useCitationTooltip';
import {
  generateQAValidation,
  generateQAValidationStream,
  getQAValidationSessions,
  updateQASessionStatus,
  getClientQAStatus,
  deleteQASession,
  getAllClients,
  submitQAFeedback
} from '../../services/api';
import { createChatStream, getApiBaseUrl, getChatHistoryWithCitations } from '../../services/api';
import { getClientDocuments, getYouTubeVideos, getKnowledgeBase, getKnowledgeBasePages } from '../../services/api';
import { convertMarkdownToSafeHtml } from '../../utils/citationHelper';
import '../../components/ChatInterface.css';

interface QAPair {
  question: string;
  answer: string;
  citations: Array<{
    id: string;
    text: string;
    position: number;
    source: {
      content: string;
      url: string;
      title: string;
    };
  }>;
  sources: Record<string, {
    content: string;
    url: string;
    title: string;
  }>;
  feedback?: {
    type: 'thumbs_up' | 'thumbs_down' | 'need_review';
    reason?: string;
    additional_comments?: string;
    created_at: string;
    created_by: string;
  };
}

interface QASession {
  id: string;
  qa_pairs: QAPair[];
  created_at: string;
  status: string;
  reviewed_by?: string;
  reviewed_at?: string;
  success_rate?: number | null;
  feedback_count?: number;
  positive_feedback_count?: number;
  has_answers?: boolean;
  questions_only?: string[];
}

interface ExpandedQuestions {
  [key: string]: boolean;
}

interface ClientStatus {
  client_id: string;
  can_generate_qa: boolean;
  content_available: boolean;
  embeddings_available: boolean;
  content_count: number;
  embeddings_count: number;
  last_crawl?: string;
  existing_sessions: number;
  latest_session?: QASession;
}

interface QAValidationProps {
  clientId?: string;
  embedded?: boolean;
}

// Add custom styles for animations
const customStyles = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fadeIn {
    animation: fadeIn 0.3s ease-out;
  }
`;

const QAValidation: React.FC<QAValidationProps> = ({ clientId: propClientId, embedded = false }) => {
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [clientStatus, setClientStatus] = useState<ClientStatus | null>(null);
  const [qaSessions, setQASessions] = useState<QASession[]>([]);
  const [selectedSession, setSelectedSession] = useState<QASession | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState<string>('');
  const [numQuestions, setNumQuestions] = useState(10);
  const [expandedQA, setExpandedQA] = useState<Set<number>>(new Set());
  const [expandedQuestions, setExpandedQuestions] = useState<ExpandedQuestions>({});
  // Live Q&A run state
  const [isLiveRunActive, setIsLiveRunActive] = useState(false);
  const [liveRunItems, setLiveRunItems] = useState<Array<{ question: string; answer: string; citations: any[]; status: 'queued'|'question'|'answering'|'done'|'error'; }>>([]);
  const [liveRunIndex, setLiveRunIndex] = useState(0);
  const [abortRunStream, setAbortRunStream] = useState<(() => void) | null>(null);
  // Keep a ref in sync with latest liveRunItems to avoid stale closures when saving
  const liveRunItemsRef = useRef<Array<{ question: string; answer: string; citations: any[]; status: 'queued'|'question'|'answering'|'done'|'error'; }>>([]);
  useEffect(() => { liveRunItemsRef.current = liveRunItems; }, [liveRunItems]);
  // Removed live custom question state - using custom queue instead
  // Custom questions queue
  const [customQueueInput, setCustomQueueInput] = useState('');
  const [customQuestionsQueue, setCustomQuestionsQueue] = useState<string[]>([]);
  const [isCustomMode, setIsCustomMode] = useState(false);
  // Source selection state
  const [activeSourceTab, setActiveSourceTab] = useState<'pages'|'documents'|'youtube'>('pages');
  const [pages, setPages] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [videos, setVideos] = useState<any[]>([]);
  const [docQuery, setDocQuery] = useState('');
  const [videoQuery, setVideoQuery] = useState('');
  const [selectedPageUrls, setSelectedPageUrls] = useState<string[]>([]);
  const [selectedDocIds, setSelectedDocIds] = useState<(string|number)[]>([]);
  const [selectedVideoIds, setSelectedVideoIds] = useState<(string|number)[]>([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pagesSearch, setPagesSearch] = useState('');
  const [totalPagesCount, setTotalPagesCount] = useState(0);
  // UI state
  const [isClientStatusCollapsed, setIsClientStatusCollapsed] = useState(false);
  // Delete session modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<QASession | null>(null);

  // Role detection
  const userRole = localStorage.getItem('user_role') || '';
  const canManageQA = ['consultant', 'super', 'client', 'client_admin'].includes(userRole);

  // Convert Q&A pairs to message format for citation tooltips (use sources map like earlier working flow)
  const qaMessages = selectedSession ? selectedSession.qa_pairs.map((qaPair, index) => {
    const citations = Object.entries(qaPair.sources || {}).map(([key, source]: any) => ({
      title: source.title || 'Source',
      url: source.url || '',
      text: source.content || '',
      cited_text: source.content || '',
      sentence: source.content || ''
    }));

    return {
      message_id: `qa-${selectedSession.id}-${index}`,
      id: `qa-${selectedSession.id}-${index}`,
      citation_sources: citations,
      metadata: {
        citations: citations,
        citation_sources: citations
      }
    };
  }) : [];

  // Use citation tooltip hook
  const { citationTooltip, tooltipEventHandlers } = useCitationTooltip(qaMessages);

  // Feedback handler for Q&A pairs
  const handleQAFeedback = async (messageId: string, type: 'thumbs_up' | 'thumbs_down' | 'need_review', reason?: string) => {
    try {
      // Extract QA pair index from messageId (format: qa-sessionId-index)
      const qaPairIndex = parseInt(messageId.split('-').pop() || '0');
      
      if (!selectedSession) {
        throw new Error('No session selected');
      }

      // Use the proper API function
      const result = await submitQAFeedback(selectedSession.id, qaPairIndex, type, reason, '');
      
      if (result.success) {
        toast.success('Feedback submitted successfully!');
        
        // Refresh the session to get updated feedback data and success rates
        await fetchQASessions();
        
        // Update the selected session if it's still the same one
        if (selectedSession && selectedSession.id === selectedSession.id) {
          // Wait for the sessions to be refreshed, then update selected session
          setTimeout(() => {
            const updatedSession = qaSessions.find(s => s.id === selectedSession.id);
            if (updatedSession) {
              setSelectedSession(updatedSession);
            }
          }, 100);
        }
      } else {
        throw new Error(result.message || 'Failed to submit feedback');
      }

    } catch (error) {
      console.error('Error submitting QA feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    }
  };

  useEffect(() => {
    if (!propClientId) {
      fetchClients();
    }
  }, [propClientId]);

  useEffect(() => {
    if (propClientId) {
      setSelectedClient(propClientId);
    }
  }, [propClientId]);

  useEffect(() => {
    if (selectedClient) {
      fetchClientStatus();
      fetchQASessions();
      fetchSources();
    }
  }, [selectedClient]);

  // Refetch pages when currentPage or pagesSearch changes
  useEffect(() => {
    if (selectedClient) {
      fetchSources();
    }
  }, [currentPage, pagesSearch]);

  const fetchClients = async () => {
    try {
      const data = await getAllClients();
      if (data.success) {
        setClients(data.clients);
      } else {
        setClients(data); // Handle if the response is directly the clients array
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Failed to fetch clients');
    }
  };

  const fetchSources = async () => {
    setLoadingSources(true);
    try {
      // Pages (Scraped content from knowledge base) - use paginated API
      try {
        const pagesResponse = await getKnowledgeBasePages(selectedClient, currentPage, 50, pagesSearch);
        console.log('Pages response:', pagesResponse);
        
        if (pagesResponse?.success && pagesResponse?.data) {
          setPages(pagesResponse.data.pages || []);
          setTotalPages(pagesResponse.data.total_pages || 0);
          setTotalPagesCount(pagesResponse.data.total || 0);
        } else {
          setPages([]);
          setTotalPages(0);
          setTotalPagesCount(0);
        }
      } catch (e) {
        console.error('Failed to load knowledge base pages:', e);
        setPages([]);
        setTotalPages(0);
        setTotalPagesCount(0);
      }
      // Documents
      try {
        const docsResponse: any = await getClientDocuments(selectedClient);
        console.log('Documents response:', docsResponse);
        let docs = [];
        // Handle new API response format
        if (docsResponse?.success && docsResponse?.data?.items) {
          docs = docsResponse.data.items.map((doc: any) => ({
            id: doc.id,
            title: doc.filename || doc.title,
            name: doc.filename,
            mime: doc.file_type,
            size: doc.file_size,
            created_at: doc.created_at
          }));
        } else if (docsResponse?.success && Array.isArray(docsResponse.data)) {
          // Handle array format [id, name, extension, size, date]
          docs = docsResponse.data.map((doc: any[]) => ({
            id: doc[0],
            name: doc[1],
            mime: doc[2],
            size: doc[3],
            created_at: doc[4]
          }));
        } else if (Array.isArray(docsResponse)) {
          // Direct array response
          docs = docsResponse.map((doc: any) => {
            if (Array.isArray(doc)) {
              return {
                id: doc[0],
                name: doc[1],
                mime: doc[2],
                size: doc[3],
                created_at: doc[4]
              };
            }
            return doc; // Already an object
          });
        }
        setDocuments(docs);
      } catch (e) {
        console.warn('Failed to load documents:', e);
        setDocuments([]);
      }
      // YouTube videos
      try {
        const vidsResponse: any = await getYouTubeVideos(selectedClient);
        console.log('YouTube videos response:', vidsResponse);
        let vids = [];
        // Handle new API response format
        if (vidsResponse?.success && vidsResponse?.data?.items) {
          vids = vidsResponse.data.items;
        } else if (Array.isArray(vidsResponse)) {
          vids = vidsResponse;
        } else if (vidsResponse?.data) {
          vids = Array.isArray(vidsResponse.data) ? vidsResponse.data : [];
        }
        setVideos(vids);
      } catch (e) {
        console.warn('Failed to load videos:', e);
        setVideos([]);
      }
    } catch (error) {
      console.error('Error fetching sources:', error);
    } finally {
      setLoadingSources(false);
    }
  };

  const addCustomQuestionToQueue = () => {
    if (!customQueueInput.trim()) return;
    setCustomQuestionsQueue(prev => [...prev, customQueueInput.trim()]);
    setCustomQueueInput('');
  };
  const removeCustomQuestionFromQueue = (idx: number) => {
    setCustomQuestionsQueue(prev => prev.filter((_, i) => i !== idx));
  };
  const clearCustomQueue = () => setCustomQuestionsQueue([]);

  const togglePage = (url: string) => {
    setSelectedPageUrls(prev => 
      prev.includes(url) ? prev.filter(u => u !== url) : [...prev, url]
    );
  };
  
  const toggleDoc = (id: string|number) => {
    setSelectedDocIds(prev => 
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    );
  };
  
  const toggleVideo = (id: string|number) => {
    setSelectedVideoIds(prev => 
      prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]
    );
  };

  // Select All functions respecting the question limit
  const selectAllPages = () => {
    if (isCustomMode) {
      // In custom mode, select all visible pages
      setSelectedPageUrls(pages.map(p => p.url).filter(Boolean));
    } else {
      // In regular mode, respect the limit
      const currentSelected = getTotalSelectedSources();
      const availableSlots = numQuestions - currentSelected + selectedPageUrls.length;
      const pagesToSelect = pages.slice(0, availableSlots).map(p => p.url).filter(Boolean);
      setSelectedPageUrls(pagesToSelect);
    }
  };

  const selectAllDocuments = () => {
    if (isCustomMode) {
      // In custom mode, select all visible documents
      const visibleDocs = documents.filter((d:any) => {
        const t = (d.title||'') + ' ' + (d.name||'') + ' ' + (String(d.id)||'');
        return t.toLowerCase().includes(docQuery.toLowerCase());
      }).slice(0, 50);
      setSelectedDocIds(visibleDocs.map(d => d.id).filter(Boolean));
    } else {
      // In regular mode, respect the limit
      const currentSelected = getTotalSelectedSources();
      const availableSlots = numQuestions - currentSelected + selectedDocIds.length;
      const visibleDocs = documents.filter((d:any) => {
        const t = (d.title||'') + ' ' + (d.name||'') + ' ' + (String(d.id)||'');
        return t.toLowerCase().includes(docQuery.toLowerCase());
      }).slice(0, availableSlots);
      setSelectedDocIds(visibleDocs.map(d => d.id).filter(Boolean));
    }
  };

  const selectAllVideos = () => {
    if (isCustomMode) {
      // In custom mode, select all visible videos
      const visibleVideos = videos.filter((v:any) => {
        const t = (v.title||'') + ' ' + (String(v.id)||'') + ' ' + (v.url||'');
        return t.toLowerCase().includes(videoQuery.toLowerCase());
      }).slice(0, 50);
      setSelectedVideoIds(visibleVideos.map(v => v.id).filter(Boolean));
    } else {
      // In regular mode, respect the limit
      const currentSelected = getTotalSelectedSources();
      const availableSlots = numQuestions - currentSelected + selectedVideoIds.length;
      const visibleVideos = videos.filter((v:any) => {
        const t = (v.title||'') + ' ' + (String(v.id)||'') + ' ' + (v.url||'');
        return t.toLowerCase().includes(videoQuery.toLowerCase());
      }).slice(0, availableSlots);
      setSelectedVideoIds(visibleVideos.map(v => v.id).filter(Boolean));
    }
  };
  const clearAllSelections = () => {
    setSelectedPageUrls([]);
    setSelectedDocIds([]);
    setSelectedVideoIds([]);
  };

  // Calculate total selected sources
  const getTotalSelectedSources = () => {
    return selectedPageUrls.length + selectedDocIds.length + selectedVideoIds.length;
  };

  // Check if selection limit is reached
  const isSelectionLimitReached = (numQuestions: number) => {
    const total = getTotalSelectedSources();
    return total >= numQuestions;
  };

  const fetchClientStatus = async () => {
    if (!selectedClient) return;

    try {
      setLoading(true);
      const data = await getClientQAStatus(selectedClient);
      if (data.success) {
        setClientStatus(data.data);
      }
    } catch (error) {
      console.error('Error fetching client status:', error);
      toast.error('Failed to fetch client status');
    } finally {
      setLoading(false);
    }
  };

  const fetchQASessions = async () => {
    if (!selectedClient) return;

    try {
      const data = await getQAValidationSessions(selectedClient);
      if (data.success) {
        setQASessions(data.data.sessions);
      }
    } catch (error) {
      console.error('Error fetching QA sessions:', error);
      toast.error('Failed to fetch QA sessions');
    }
  };

  // Removed askCustomQuestion and cancelStreaming - using custom queue instead

  const generateQA = async () => {
    if (!selectedClient) return;

    // Check if any sources are selected
    if (!isCustomMode && selectedPageUrls.length === 0 && selectedDocIds.length === 0 && selectedVideoIds.length === 0) {
      toast.error('Please select at least one source (page, document, or video)');
      return;
    }

    setGenerating(true);
    setGenerationProgress('Starting...');

    // Debug log to see what we're sending
    console.log('Generating questions with params:', {
      clientId: selectedClient,
      numQuestions,
      selectedPageUrls,
      selectedDocIds,
      selectedVideoIds,
      customQuestionsQueue,
      options: {
        questions_only: true,
        use_selected_sources: true
      }
    });

    generateQAValidationStream(
      selectedClient,
      numQuestions,
      {
        source_filters: {
          page_urls: selectedPageUrls,
          document_ids: selectedDocIds,
          youtube_ids: selectedVideoIds
        },
        generation_mode: 'combined',
        ...(customQuestionsQueue.length ? { custom_questions: customQuestionsQueue } : {}),
        options: {
          questions_only: true,
          use_selected_sources: true
        }
      },
      {
        onProgress: (data) => {
          console.log('QA Generation progress:', data);
          setGenerationProgress(data.status || `Processing...`);
        },
        onQAPair: (data) => {
          console.log('QA Pair received:', data);
          setGenerationProgress(`Generated question ${data.index + 1}/${data.total}`);
        },
        onComplete: async (data) => {
          console.log('QA Generation complete:', data);
          setGenerating(false);
          setGenerationProgress('');
          toast.success('Questions generated successfully! Click "Generate Answers" on the session to get answers.');
          await fetchQASessions();
          await fetchClientStatus();
          // Auto-select the new session
          if (data.qa_session_id) {
            setTimeout(() => {
              const newSession = qaSessions.find(s => s.id === data.qa_session_id);
              if (newSession) {
                setSelectedSession(newSession);
              }
            }, 500);
          }
          // Clear custom questions after successful generation
          if (customQuestionsQueue.length > 0) {
            setCustomQuestionsQueue([]);
            setIsCustomMode(false);
          }
        },
        onError: (error) => {
          console.error('QA Generation error:', error);
          setGenerating(false);
          setGenerationProgress('');
          toast.error(error || 'Failed to generate questions');
        }
      }
    );
  };

  const createCustomQuestionsSession = async () => {
    if (!selectedClient) return;
    if (customQuestionsQueue.length === 0) {
      toast.error('Add at least one custom question');
      return;
    }
    try {
      setGenerating(true);
      const payload = {
        source_filters: (selectedPageUrls.length || selectedDocIds.length || selectedVideoIds.length) ? {
          page_urls: selectedPageUrls,
          document_ids: selectedDocIds,
          youtube_ids: selectedVideoIds
        } : undefined,
        generation_mode: 'combined',
        custom_questions: customQuestionsQueue,
        options: { questions_only: true }
      } as any;
      const data = await generateQAValidation(selectedClient, customQuestionsQueue.length, payload);
      if (data.success) {
        toast.success('Session created from custom questions');
        await fetchQASessions();
        await fetchClientStatus();
        if (data.data.qa_session_id) {
          setTimeout(() => {
            const newSession = qaSessions.find(s => s.id === data.data.qa_session_id);
            if (newSession) setSelectedSession(newSession);
          }, 500);
        }
        clearCustomQueue();
        setIsCustomMode(false);
      } else {
        toast.error(data.message || 'Failed to create session');
      }
    } catch (error) {
      console.error('Error creating custom questions session:', error);
      toast.error('Failed to create session');
    } finally {
      setGenerating(false);
    }
  };

  const startLiveRun = async () => {
    if (!selectedClient || isLiveRunActive || !selectedSession) return;
    
    console.log('Starting live run for session:', selectedSession);
    
    // Get questions from the selected session
    const questions = selectedSession.qa_pairs.map(p => p.question).filter(Boolean);
    
    if (questions.length === 0) {
      toast.error('No questions found in this session');
      return;
    }
    
    console.log('Questions to process:', questions);
    
    try {
      setIsLiveRunActive(true);
      setLiveRunItems([]);
      setLiveRunIndex(0);
      
      // Initialize live items with questions visible
      const initialItems = questions.map(q => ({ 
        question: q, 
        answer: '', 
        citations: [], 
        status: 'queued' as 'queued' | 'question' | 'answering' | 'done' | 'error'
      }));
      setLiveRunItems(initialItems);
      
      // Small delay to ensure UI updates
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Stream answers sequentially with better UX
      for (let i = 0; i < questions.length; i++) {
        // Check if cancelled
        const currentRunState = await new Promise<boolean>(resolve => {
          setIsLiveRunActive(current => {
            resolve(current);
            return current;
          });
        });
        
        if (!currentRunState) {
          console.log('Live run cancelled');
          break;
        }
        
        setLiveRunIndex(i);
        
        console.log(`Processing question ${i + 1}/${questions.length}: ${questions[i]}`);
        
        // Smooth transition to answering state
        setLiveRunItems(prev => prev.map((it, idx) => {
          if (idx === i) return { ...it, status: 'answering', answer: '', citations: [] };
          if (idx < i && it.status === 'queued') return { ...it, status: 'done' };
          return it;
        }));
        
        // Auto-scroll to current question
        setTimeout(() => {
          const element = document.getElementById(`live-qa-item-${i}`);
          const container = document.getElementById('live-qa-container');
          if (element && container) {
            const elementTop = element.offsetTop;
            const elementHeight = element.offsetHeight;
            const containerHeight = container.offsetHeight;
            const scrollTop = elementTop - (containerHeight / 2) + (elementHeight / 2);
            
            container.scrollTo({
              top: Math.max(0, scrollTop),
              behavior: 'smooth'
            });
          }
        }, 100);
        
        const q = questions[i];
        let currentAnswer = '';
        let streamStarted = false;
        
        await new Promise<void>((resolve, reject) => {
          console.log('Creating chat stream for question:', q);
          
          const stop = createChatStream(
            selectedClient,
            q,
            (chunk) => {
              streamStarted = true;
              console.log('Received chunk:', chunk);
              
              if (chunk?.type === 'text_chunk' && chunk?.content) {
                currentAnswer += chunk.content;
                console.log(`Question ${i}: Current answer length: ${currentAnswer.length}`);
                setLiveRunItems(prev => prev.map((it, idx) => 
                  idx === i ? { ...it, answer: currentAnswer } : it
                ));
              }
            },
            (complete) => {
              console.log('Stream complete:', complete);
              
              let cites = complete?.citation_sources || complete?.metadata?.citation_sources || complete?.citations || complete?.metadata?.citations || [];
              // Use the complete content if available, otherwise use accumulated answer
              const finalAnswer = complete?.content || currentAnswer || '';
              console.log('Final answer length:', finalAnswer.length);
              
              setLiveRunItems(prev => prev.map((it, idx) => 
                idx === i ? { 
                  ...it, 
                  answer: finalAnswer, 
                  status: 'done', 
                  citations: Array.isArray(cites) ? cites : [] 
                } : it
              ));
              setAbortRunStream(null);
              // Persist this answer immediately to backend (per-question save)
              (async () => {
                try {
                  if (selectedSession?.id) {
                    // Build sources map from citations if available
                    const sourcesFromCitations: Record<string, { content: string; url: string; title: string; }> = {};
                    if (Array.isArray(cites) && cites.length > 0) {
                      cites.forEach((c: any, idx: number) => {
                        const src = (c && c.source) ? c.source : c || {};
                        const key = `source_${idx + 1}`;
                        sourcesFromCitations[key] = {
                          content: c?.text || c?.cited_text || c?.sentence || src?.content || '',
                          url: c?.url || src?.url || '',
                          title: c?.title || src?.title || `Source ${idx + 1}`
                        };
                      });
                    }
                    // If still no citations (some flows), try grabbing the very last chat with citations
                    if ((!cites || cites.length === 0) && selectedClient) {
                      try {
                        const hist: any = await getChatHistoryWithCitations(selectedClient, { page: 1, limit: 1 });
                        const arr = Array.isArray(hist?.data?.items) ? hist.data.items
                                  : Array.isArray(hist?.data?.messages) ? hist.data.messages
                                  : Array.isArray(hist?.messages) ? hist.messages
                                  : Array.isArray(hist?.items) ? hist.items
                                  : (Array.isArray(hist) ? hist : []);
                        const last = Array.isArray(arr) && arr.length ? arr[0] : null;
                        const lastCites = last?.metadata?.citation_sources || last?.citation_sources || last?.citations || [];
                        if (Array.isArray(lastCites) && lastCites.length > 0) {
                          cites = lastCites;
                          // Rebuild sources from fallback cites
                          Object.keys(sourcesFromCitations).forEach(k => delete (sourcesFromCitations as any)[k]);
                          cites.forEach((c: any, idx: number) => {
                            const src = (c && c.source) ? c.source : c || {};
                            const key = `source_${idx + 1}`;
                            sourcesFromCitations[key] = {
                              content: c?.text || c?.cited_text || c?.sentence || src?.content || '',
                              url: c?.url || src?.url || '',
                              title: c?.title || src?.title || `Source ${idx + 1}`
                            };
                          });
                        }
                      } catch (e) {
                        console.warn('Citation fallback fetch failed:', e);
                      }
                    }
                    const saveResp = await fetch(`${getApiBaseUrl()}/qa-validation/session/${selectedSession.id}/qa/${i}`, {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                      },
                      body: JSON.stringify({
                        answer: finalAnswer,
                        citations: Array.isArray(cites) ? cites : [],
                        sources: Object.keys(sourcesFromCitations).length ? sourcesFromCitations : undefined
                      })
                    });
                    if (!saveResp.ok) {
                      console.error('Per-question save failed', await saveResp.text());
                    } else if ((!cites || cites.length === 0) || Object.keys(sourcesFromCitations).length === 0) {
                      // If still no sources/citations, trigger a server-side backfill for this session
                      try {
                        await fetch(`${getApiBaseUrl()}/qa-validation/session/${selectedSession.id}/backfill-sources`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                          }
                        });
                      } catch (e) {
                        console.warn('Backfill request failed (per-question):', e);
                      }
                    }
                  }
                } catch (e) {
                  console.error('Error during per-question save:', e);
                }
              })();
              resolve();
            },
            (error) => {
              console.error('Live run stream error:', error);
              setLiveRunItems(prev => prev.map((it, idx) => 
                idx === i ? { ...it, status: 'error', answer: error?.message || 'Error generating response' } : it
              ));
              setAbortRunStream(null);
              resolve();
            },
            {
              qa_run: true,
              force_embeddings: true,
              force_citations: true,
              avoid_calendar: true
            },
            `${getApiBaseUrl()}/qa-validation/chat/stream`
          );
          
          // Set abort function
          setAbortRunStream(() => stop);
          
          // Timeout fallback if stream doesn't start
          setTimeout(() => {
            if (!streamStarted) {
              console.error('Stream timeout - no response received');
              setLiveRunItems(prev => prev.map((it, idx) => 
                idx === i ? { ...it, status: 'error', answer: 'Timeout: No response from server' } : it
              ));
              resolve();
            }
          }, 30000); // 30 second timeout
        });
        
        // Small delay between questions
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      setIsLiveRunActive(false);
      setAbortRunStream(null);
      
      // Skip bulk save: per-question saves are already persisted during the run.
      // This avoids overwriting citations/sources with stale local state.
      
      // Fetch updated sessions
      await fetchQASessions();
      
      // Wait a bit for the backend to update, then refresh
      setTimeout(async () => {
        // Fetch sessions again to get the updated data
        const sessionsResponse = await getQAValidationSessions(selectedClient);
        console.log('Fetched sessions after save:', sessionsResponse);
        if (sessionsResponse.success) {
          const updatedSessions = sessionsResponse.data.sessions;
          setQASessions(updatedSessions);
          
          // Find and update the selected session
          const updatedSession = updatedSessions.find((s: QASession) => s.id === selectedSession.id);
          console.log('Updated session found:', updatedSession);
          if (updatedSession) {
            setSelectedSession(updatedSession);
          }
        }
        
        // Clear live run items to return to normal session view
        setLiveRunItems([]);
      }, 1500);
      
      // Show completion message
      toast.success(`Q&A generation completed!`);
    } catch (e) {
      console.error('Live run failed:', e);
      setIsLiveRunActive(false);
      setAbortRunStream(null);
      toast.error('Failed to generate answers');
    }
  };

  const cancelLiveRun = () => {
    try { abortRunStream && abortRunStream(); } catch {}
    setIsLiveRunActive(false);
    setAbortRunStream(null);
    toast.info('Live Q&A generation stopped');
  };

  const updateSessionStatus = async (sessionId: string, status: string) => {
    try {
      const data = await updateQASessionStatus(
        sessionId, 
        status, 
        localStorage.getItem('user_id') || 'consultant'
      );
      if (data.success) {
        toast.success(data.message);
        await fetchQASessions();
        // Update selected session if it was the one we updated
        if (selectedSession && selectedSession.id === sessionId) {
          const updatedSession = qaSessions.find(s => s.id === sessionId);
          if (updatedSession) {
            setSelectedSession({ ...updatedSession, status });
          }
        }
      } else {
        toast.error('Failed to update session status');
      }
    } catch (error) {
      console.error('Error updating session status:', error);
      toast.error('Failed to update session status');
    }
  };

  const hardDeleteSelectedSession = async () => {
    if (!sessionToDelete) return;
    try {
      const data = await deleteQASession(sessionToDelete.id);
      if (data.success) {
        toast.success('QA session deleted');
        setShowDeleteModal(false);
        setSessionToDelete(null);
        await fetchQASessions();
        if (selectedSession && selectedSession.id === sessionToDelete.id) {
          setSelectedSession(null);
        }
      } else {
        toast.error(data.message || 'Failed to delete session');
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error('Failed to delete session');
    }
  };

  const toggleQAExpansion = (index: number) => {
    const newExpanded = new Set(expandedQA);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedQA(newExpanded);
  };

  const toggleQuestionExpansion = (questionKey: string) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionKey]: !prev[questionKey]
    }));
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />;
      case 'rejected':
        return <FontAwesomeIcon icon={faTimesCircle} className="text-red-500" />;
      case 'needs_review':
        return <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-500" />;
      default:
        return <FontAwesomeIcon icon={faClock} className="text-gray-500" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const copySessionToClipboard = (session: QASession) => {
    // Format session data for copying
    let sessionText = `QA Session - ${formatDate(session.created_at)}\n`;
    sessionText += `Status: ${session.status}\n`;
    sessionText += `Questions: ${session.qa_pairs.length}\n\n`;
    
    session.qa_pairs.forEach((qaPair, index) => {
      sessionText += `Question ${index + 1}:\n${qaPair.question}\n\n`;
      sessionText += `Answer:\n${qaPair.answer}\n\n`;
      
      // Add sources
      if (Object.keys(qaPair.sources).length > 0) {
        sessionText += `Sources:\n`;
        Object.entries(qaPair.sources).forEach(([key, source]) => {
          const sourceNumber = key.replace('source_', '');
          sessionText += `[${sourceNumber}] ${source.title}\n`;
          sessionText += `${source.content}\n`;
          if (source.url) {
            sessionText += `URL: ${source.url}\n`;
          }
          sessionText += '\n';
        });
      }
      sessionText += '---\n\n';
    });
    
    navigator.clipboard.writeText(sessionText).then(() => {
      toast.success('Session copied to clipboard!');
    }).catch((err) => {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy session');
    });
  };

  const renderCitationSources = (sources: Record<string, any>) => {
    return Object.entries(sources).map(([key, source]) => {
      // Extract source number from key (e.g., "source_1" -> "1")
      const sourceNumber = key.replace('source_', '');
      const htmlContent = convertMarkdownToSafeHtml(source.content);
      
      return (
        <div key={key} className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-2">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold mr-2">
                  {sourceNumber}
                </span>
                <h4 className="font-semibold text-sm text-gray-900">
                  {source.title}
                </h4>
              </div>
              <div 
                className="text-xs text-gray-600 mb-2 line-clamp-3 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
              {source.url && (
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-xs flex items-center"
                >
                  <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                  View Source
                </a>
              )}
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: customStyles }} />
      <div className={embedded ? "" : "min-h-screen bg-gray-50 p-6"}>
        <div className={embedded ? "" : "max-w-7xl mx-auto"}>
        {/* Header - Hide when embedded */}
        {!embedded && (
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <FontAwesomeIcon icon={faQuestionCircle} className="mr-3 text-blue-500" />
                Q&A Validation
              </h1>
              <p className="text-gray-600 mt-1">
                Test your chatbot's knowledge with automated question generation and live validation
              </p>
            </div>
            <div className="text-sm text-gray-500 bg-gray-50 px-3 py-1.5 rounded-full">
              <FontAwesomeIcon icon={faInfoCircle} className="mr-1.5" />
              Validate bot responses before deployment
            </div>
          </div>
        </div>
        )}

        {/* Client Selection Bar - Hide when embedded with propClientId */}
        {!embedded && (
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <FontAwesomeIcon icon={faUser} className="mr-2 text-gray-500" />
                Client
              </h2>
              <select
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Choose a client...</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name} ({client.email})
                  </option>
                ))}
              </select>
            </div>
            {selectedClient && clientStatus && (
              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <div className="text-xl font-bold text-gray-900">{clientStatus.embeddings_count}</div>
                  <div className="text-xs text-gray-500">Indexed</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-gray-900">{clientStatus.existing_sessions}</div>
                  <div className="text-xs text-gray-500">Sessions</div>
                </div>
                <div className="flex items-center">
                  {clientStatus.can_generate_qa ? (
                    <div className="bg-green-100 rounded-full px-3 py-1 flex items-center">
                      <FontAwesomeIcon icon={faCheckCircle} className="mr-1 text-green-600 text-sm" />
                      <span className="text-sm font-medium text-green-700">Ready</span>
                    </div>
                  ) : (
                    <div className="bg-red-100 rounded-full px-3 py-1 flex items-center">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1 text-red-600 text-sm" />
                      <span className="text-sm font-medium text-red-700">Not Ready</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {selectedClient && clientStatus && clientStatus.last_crawl && (
            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
              <div className="text-xs text-gray-500">
                Last crawl: {formatDate(clientStatus.last_crawl)}
              </div>
              {!clientStatus.can_generate_qa && (
                <div className="text-xs bg-yellow-50 text-yellow-700 rounded px-2 py-1">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                  Needs content and embeddings for Q&A generation
                </div>
              )}
            </div>
          )}
        </div>
        )}


        {selectedClient && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Sidebar - Sources & Questions */}
            <div className="lg:col-span-1 space-y-4">

              {/* Question Configuration - MOVED TO TOP - Only for consultants/super */}
              {canManageQA && (
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <h3 className="text-md font-semibold text-gray-900 mb-3">Configure Questions</h3>
                
                {/* Toggle between modes */}
                <div className="flex rounded-lg border border-gray-200 p-1 mb-3">
                  <button
                    onClick={() => {
                      setIsCustomMode(false);
                      setCustomQuestionsQueue([]);
                    }}
                    className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-all flex items-center justify-center ${
                      !isCustomMode
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                    }`}
                  >
                    <FontAwesomeIcon icon={faRobot} className="mr-1 text-xs" />
                    AI Generated
                  </button>
                  <button
                    onClick={() => setIsCustomMode(true)}
                    className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-all flex items-center justify-center ${
                      isCustomMode
                        ? 'bg-green-600 text-white'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                    }`}
                  >
                    <FontAwesomeIcon icon={faUser} className="mr-1 text-xs" />
                    Custom
                  </button>
                </div>
                
                {/* AI Generation Mode */}
                {!isCustomMode && (
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Number of questions
                      </label>
                      <select
                        value={numQuestions}
                        onChange={(e) => setNumQuestions(parseInt(e.target.value))}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value={5}>5 Questions</option>
                        <option value={10}>10 Questions</option>
                        <option value={15}>15 Questions</option>
                        <option value={20}>20 Questions</option>
                        <option value={25}>25 Questions</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Select up to {numQuestions} sources. "Select All" will choose the first {numQuestions} available.
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Custom Questions Mode */}
                {isCustomMode && (
                  <div className="space-y-2">
                    {customQuestionsQueue.length > 0 ? (
                      <div className="p-3 bg-green-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xs font-medium text-gray-900">
                            {customQuestionsQueue.length} Question{customQuestionsQueue.length !== 1 ? 's' : ''}
                          </h3>
                          <button 
                            onClick={clearCustomQueue} 
                            className="text-xs text-red-600 hover:text-red-800"
                          >
                            Clear
                          </button>
                        </div>
                        
                        <ul className="space-y-1 max-h-32 overflow-auto">
                          {customQuestionsQueue.map((q, idx) => (
                            <li key={idx} className="flex items-start justify-between gap-1 bg-white p-1.5 rounded border border-gray-200 text-xs">
                              <span className="text-gray-800 flex-1">{idx + 1}. {q}</span>
                              <button
                                onClick={() => removeCustomQuestionFromQueue(idx)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <FontAwesomeIcon icon={faTrash} className="text-xs" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div className="p-3 bg-gray-50 rounded-lg text-center">
                        <p className="text-xs text-gray-600">No questions added</p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Add Custom Question Input - Only show in custom mode */}
                {isCustomMode && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Add question
                    </label>
                    <div className="flex gap-1">
                      <input
                        value={customQueueInput}
                        onChange={(e) => setCustomQueueInput(e.target.value)}
                        placeholder="Type your question..."
                        className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && customQueueInput.trim()) {
                            e.preventDefault();
                            addCustomQuestionToQueue();
                          }
                        }}
                      />
                      <button
                        onClick={addCustomQuestionToQueue}
                        disabled={!customQueueInput.trim()}
                        className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                )}
              </div>
              )}

              {/* Source Selection - Only for consultants/super */}
              {canManageQA && (
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setActiveSourceTab('pages')} className={`px-3 py-1.5 text-sm rounded transition-colors ${activeSourceTab==='pages'?'bg-blue-600 text-white':'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>Website</button>
                    <button onClick={() => setActiveSourceTab('documents')} className={`px-3 py-1.5 text-sm rounded transition-colors ${activeSourceTab==='documents'?'bg-blue-600 text-white':'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>Documents</button>
                    <button onClick={() => setActiveSourceTab('youtube')} className={`px-3 py-1.5 text-sm rounded transition-colors ${activeSourceTab==='youtube'?'bg-blue-600 text-white':'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>YouTube</button>
                  </div>
                  {!isCustomMode && (
                    <div className={`text-sm px-2 py-1 rounded ${
                      getTotalSelectedSources() > numQuestions 
                        ? 'bg-red-100 text-red-700' 
                        : getTotalSelectedSources() === numQuestions
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {getTotalSelectedSources()}/{numQuestions}
                    </div>
                  )}
                </div>
                {/* Loading indicator */}
                {loadingSources ? (
                  <div className="flex items-center justify-center py-8">
                    <FontAwesomeIcon icon={faSpinner} className="text-blue-600 text-2xl animate-spin" />
                    <span className="ml-3 text-gray-600">Loading sources...</span>
                  </div>
                ) : (
                  <>
                {/* Search */}
                {activeSourceTab === 'pages' && (
                  <>
                    <div className="flex gap-2 mb-2">
                      <input 
                        value={pagesSearch} 
                        onChange={(e) => {
                          setPagesSearch(e.target.value);
                          setCurrentPage(1); // Reset to first page when searching
                        }} 
                        placeholder="Search pages..." 
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={selectAllPages}
                        disabled={!isCustomMode && getTotalSelectedSources() >= numQuestions}
                        className="px-3 py-2 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Select All
                      </button>
                    </div>
                    <div className="max-h-40 overflow-auto border border-gray-200 rounded">
                      {pages.length === 0 ? (
                        <div className="px-3 py-4 text-sm text-gray-500 text-center">No pages found</div>
                      ) : (
                        (pages || []).map((p:any, i:number)=>{
                          const url = p.url || '';
                          const title = p.title || 'Untitled Page';
                          const checked = selectedPageUrls.includes(url);
                          return (
                            <label key={i} className={`flex items-center gap-2 px-3 py-2 text-sm border-b last:border-b-0 ${
                              !isCustomMode && isSelectionLimitReached(numQuestions) && !checked 
                                ? 'opacity-50 cursor-not-allowed' 
                                : 'cursor-pointer hover:bg-gray-50'
                            }`}>
                              <input 
                                type="checkbox" 
                                checked={checked} 
                                onChange={()=>togglePage(url)} 
                                className="text-blue-600" 
                                disabled={!url || (!isCustomMode && isSelectionLimitReached(numQuestions) && !checked)} 
                              />
                              <div className="truncate flex-1">
                                <div className="font-medium text-gray-900 truncate">{title}</div>
                              </div>
                              {url && (
                                <a 
                                  href={url} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="text-blue-600 hover:text-blue-800 ml-2 flex-shrink-0"
                                  onClick={(e) => e.stopPropagation()}
                                  title={url}
                                >
                                  <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs" />
                                </a>
                              )}
                            </label>
                          );
                        })
                      )}
                    </div>
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between px-3 py-2 border-t">
                        <div className="text-sm text-gray-600">
                          Page {currentPage} of {totalPages} ({totalPagesCount} total)
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {activeSourceTab === 'documents' && (
                  <>
                    <div className="flex gap-2 mb-2">
                      <input 
                        value={docQuery} 
                        onChange={(e)=>setDocQuery(e.target.value)} 
                        placeholder="Search documents..." 
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={selectAllDocuments}
                        disabled={!isCustomMode && getTotalSelectedSources() >= numQuestions}
                        className="px-3 py-2 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Select All
                      </button>
                    </div>
                    <div className="max-h-40 overflow-auto border border-gray-200 rounded">
                      {documents.length === 0 ? (
                        <div className="px-3 py-4 text-sm text-gray-500 text-center">No documents found</div>
                      ) : (
                        (documents || []).filter((d:any)=>{
                        const t = (d.title||'') + ' ' + (d.name||'') + ' ' + (String(d.id)||'');
                        return t.toLowerCase().includes(docQuery.toLowerCase());
                      }).slice(0,50).map((d:any, i:number)=>{
                        const id = d.id;
                        const title = d.title || d.name || (id ? `Document ${id}` : 'Untitled Document');
                        const checked = selectedDocIds.includes(id);
                        return (
                          <label key={i} className={`flex items-center gap-2 px-3 py-2 text-sm border-b last:border-b-0 ${
                            !isCustomMode && isSelectionLimitReached(numQuestions) && !checked 
                              ? 'opacity-50 cursor-not-allowed' 
                              : 'cursor-pointer hover:bg-gray-50'
                          }`}>
                            <input 
                              type="checkbox" 
                              checked={checked} 
                              onChange={()=>toggleDoc(id)} 
                              className="text-blue-600" 
                              disabled={!id || (!isCustomMode && isSelectionLimitReached(numQuestions) && !checked)} 
                            />
                            <div className="truncate flex-1">
                              <div className="font-medium text-gray-900 truncate">{title}</div>
                              {d.mime && (<div className="text-gray-500 text-xs truncate">{d.mime}</div>)}
                            </div>
                          </label>
                        );
                      })
                      )}
                    </div>
                  </>
                )}
                {activeSourceTab === 'youtube' && (
                  <>
                    <div className="flex gap-2 mb-2">
                      <input 
                        value={videoQuery} 
                        onChange={(e)=>setVideoQuery(e.target.value)} 
                        placeholder="Search videos..." 
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={selectAllVideos}
                        disabled={!isCustomMode && getTotalSelectedSources() >= numQuestions}
                        className="px-3 py-2 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Select All
                      </button>
                    </div>
                    <div className="max-h-40 overflow-auto border border-gray-200 rounded">
                      {videos.length === 0 ? (
                        <div className="px-3 py-4 text-sm text-gray-500 text-center">No videos found</div>
                      ) : (
                        (videos || []).filter((v:any)=>{
                        const t = (v.title||'') + ' ' + (String(v.id)||'') + ' ' + (v.url||'');
                        return t.toLowerCase().includes(videoQuery.toLowerCase());
                      }).slice(0,50).map((v:any, i:number)=>{
                        const id = v.id;
                        const title = v.title || (id ? `Video ${id}` : 'Untitled Video');
                        const checked = selectedVideoIds.includes(id);
                        return (
                          <label key={i} className={`flex items-center gap-2 px-3 py-2 text-sm border-b last:border-b-0 ${
                            !isCustomMode && isSelectionLimitReached(numQuestions) && !checked 
                              ? 'opacity-50 cursor-not-allowed' 
                              : 'cursor-pointer hover:bg-gray-50'
                          }`}>
                            <input 
                              type="checkbox" 
                              checked={checked} 
                              onChange={()=>toggleVideo(id)} 
                              className="text-blue-600" 
                              disabled={!isCustomMode && isSelectionLimitReached(numQuestions) && !checked}
                            />
                            <div className="truncate flex-1">
                              <div className="font-medium text-gray-900 truncate">{title}</div>
                              {v.url && (<div className="text-gray-500 text-xs truncate">{v.url}</div>)}
                            </div>
                          </label>
                        );
                      })
                      )}
                    </div>
                  </>
                )}
                {/* Selected chips */}
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-700">Selected</span>
                    <button onClick={clearAllSelections} className="text-xs text-gray-600 hover:text-gray-800">Clear all</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedPageUrls.map((u) => {
                      const page = pages.find((p:any) => p.url === u);
                      const displayTitle = page?.title || u.split('/').pop()?.replace(/[-_]/g, ' ') || 'Page';
                      return (
                        <span key={u} className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded border border-blue-200 truncate max-w-[180px] cursor-pointer hover:bg-blue-100" title={u} onClick={()=>togglePage(u)}>
                          {displayTitle}
                        </span>
                      );
                    })}
                    {selectedDocIds.map((id) => {
                      const doc = documents.find((d:any) => d.id === id);
                      const displayName = doc?.name || `Document ${id}`;
                      return (
                        <span key={`doc-${id}`} className="px-2 py-1 text-xs bg-green-50 text-green-700 rounded border border-green-200 truncate max-w-[180px] cursor-pointer hover:bg-green-100" title={displayName} onClick={()=>toggleDoc(id)}>
                          {displayName}
                        </span>
                      );
                    })}
                    {selectedVideoIds.map((id) => (
                      <span key={`vid-${id}`} className="px-2 py-1 text-xs bg-purple-50 text-purple-700 rounded border border-purple-200 truncate max-w-[180px] cursor-pointer hover:bg-purple-100" onClick={()=>toggleVideo(id)}>Video {id ? `#${id}` : ''}</span>
                    ))}
                  </div>
                </div>
                {/* Selection limit info */}
                {!isCustomMode && getTotalSelectedSources() > 0 && (
                  <div className={`mt-2 text-xs ${
                    getTotalSelectedSources() > numQuestions ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                    {getTotalSelectedSources() > numQuestions 
                      ? `Too many sources selected! Please select at most ${numQuestions} sources for ${numQuestions} questions.`
                      : `Each selected source will get ~${Math.ceil(numQuestions / getTotalSelectedSources())} question${Math.ceil(numQuestions / getTotalSelectedSources()) !== 1 ? 's' : ''}.`
                    }
                  </div>
                )}
                </>
                )}
              </div>
              )}


              {/* Generate/Create Session Buttons - Only for consultants/super */}
              {canManageQA && clientStatus?.can_generate_qa && (
                <div className="bg-white rounded-lg shadow-sm border p-4">
                  {!isCustomMode ? (
                    <>
                      <button
                        onClick={generateQA}
                        disabled={
                          generating || 
                          getTotalSelectedSources() === 0 ||
                          getTotalSelectedSources() > numQuestions
                        }
                        className="w-full bg-blue-600 text-white px-4 py-2.5 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors text-sm font-medium"
                      >
                        {generating ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                            {generationProgress || 'Generating...'}
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faPlus} className="mr-2" />
                            Generate Questions
                          </>
                        )}
                      </button>
                      {getTotalSelectedSources() === 0 && (
                        <p className="text-xs text-gray-500 mt-2 text-center">Select at least one source</p>
                      )}
                      {getTotalSelectedSources() > numQuestions && (
                        <p className="text-xs text-red-600 mt-2 text-center">
                          Too many sources selected! Select at most {numQuestions} sources.
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <button
                        onClick={createCustomQuestionsSession}
                        disabled={generating || customQuestionsQueue.length === 0}
                        className="w-full bg-green-600 text-white px-4 py-2.5 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors text-sm font-medium"
                      >
                        {generating ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                            Creating Session...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faPlus} className="mr-2" />
                            Create Session from Custom Questions
                          </>
                        )}
                      </button>
                      {customQuestionsQueue.length === 0 && (
                        <p className="text-xs text-gray-500 mt-2 text-center">Add at least one question</p>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Sessions List */}
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-md font-semibold text-gray-900">Sessions</h3>
                  <button
                    onClick={fetchQASessions}
                    className="text-blue-600 hover:text-blue-800 text-xs"
                  >
                    <FontAwesomeIcon icon={faSpinner} className="text-xs" />
                  </button>
                </div>
                
                {qaSessions.length === 0 ? (
                  <p className="text-gray-500 text-sm">No QA sessions yet</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {qaSessions.map((session) => (
                      <div
                        key={session.id}
                        className={`p-3 border rounded cursor-pointer transition-colors ${
                          selectedSession?.id === session.id
                            ? 'border-blue-300 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        onClick={() => setSelectedSession(session)}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-900">
                              {session.qa_pairs.length} Questions
                            </span>
                            {!session.qa_pairs.some(q => q.answer) && (
                              <span className="ml-1 text-xs px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded">
                                No answers
                              </span>
                            )}
                          </div>
                          {canManageQA && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSessionToDelete(session);
                              setShowDeleteModal(true);
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <FontAwesomeIcon icon={faTrash} className="text-xs" />
                          </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          {formatDate(session.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>

            {/* Right Side - Session View */}
            <div className="lg:col-span-2">
              {/* Live Q&A Stream Panel - Show when active */}
              {isLiveRunActive ? (
                <div className="bg-white rounded-lg shadow-lg border p-6 transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 mb-1 flex items-center">
                        <FontAwesomeIcon icon={faStream} className="mr-2 text-indigo-500" />
                        Live Answer Generation
                      </h2>
                      <p className="text-sm text-gray-500">
                        Generating answers for {selectedSession?.qa_pairs.length || 0} questions
                      </p>
                    </div>
                    {isLiveRunActive && (
                      <button 
                        onClick={cancelLiveRun} 
                        className="px-4 py-2 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors"
                      >
                        <FontAwesomeIcon icon={faTimesCircle} className="mr-1" />
                        Stop Generation
                      </button>
                    )}
                  </div>
                  
                  <div className="space-y-4 max-h-[600px] overflow-auto pr-2" id="live-qa-container">
                    {liveRunItems.map((item, idx) => {
                      const isActive = idx === liveRunIndex && isLiveRunActive;
                      const isPending = idx > liveRunIndex;
                      
                      return (
                        <div 
                          key={idx} 
                          id={`live-qa-item-${idx}`}
                          className={`border rounded-lg overflow-hidden transition-all duration-300 transform ${
                            isActive ? 'border-indigo-400 shadow-lg scale-[1.02]' :
                            item.status === 'done' ? 'border-green-300 shadow-sm' :
                            item.status === 'error' ? 'border-red-300 shadow-sm' :
                            'border-gray-200 opacity-60'
                          }`}
                        >
                          {/* Question Header */}
                          <div className={`px-4 py-3 transition-colors duration-300 ${
                            isActive ? 'bg-indigo-50' :
                            item.status === 'done' ? 'bg-green-50' :
                            item.status === 'error' ? 'bg-red-50' :
                            'bg-gray-50'
                          }`}>
                            <div className="flex items-start justify-between">
                              <div className="flex items-start space-x-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                                  isActive ? 'bg-indigo-600 text-white animate-pulse' :
                                  item.status === 'done' ? 'bg-green-600 text-white' :
                                  item.status === 'error' ? 'bg-red-600 text-white' :
                                  'bg-gray-300 text-gray-600'
                                }`}>
                                  {isActive ? (
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                  ) : item.status === 'done' ? (
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                  ) : item.status === 'error' ? (
                                    <FontAwesomeIcon icon={faTimesCircle} />
                                  ) : (
                                    <span className="text-sm font-semibold">{idx + 1}</span>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <p className={`text-sm font-medium transition-colors duration-300 ${
                                    isActive ? 'text-indigo-900' :
                                    item.status === 'done' ? 'text-green-900' :
                                    item.status === 'error' ? 'text-red-900' :
                                    'text-gray-700'
                                  }`}>
                                    {item.question || 'Generating question...'}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center">
                                {isPending && (
                                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                    <FontAwesomeIcon icon={faClock} className="mr-1" />
                                    Queued
                                  </span>
                                )}
                                {isActive && (
                                  <div className="flex items-center text-xs text-indigo-600 bg-indigo-100 px-3 py-1 rounded-full">
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1.5" />
                                    Generating...
                                  </div>
                                )}
                                {item.status === 'done' && (
                                  <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                                    <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                                    Complete
                                  </span>
                                )}
                                {item.status === 'error' && (
                                  <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded-full">
                                    <FontAwesomeIcon icon={faTimesCircle} className="mr-1" />
                                    Error
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Answer Content */}
                          <div className={`transition-all duration-300 ${
                            (item.answer || isActive) ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                          } overflow-hidden`}>
                            <div className="px-4 py-3 border-t border-gray-100">
                              <div className="flex items-start space-x-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                                  isActive ? 'bg-indigo-100' : 'bg-green-100'
                                }`}>
                                  <FontAwesomeIcon 
                                    icon={faRobot} 
                                    className={`text-sm ${isActive ? 'text-indigo-600' : 'text-green-600'}`} 
                                  />
                                </div>
                                <div className="flex-1">
                                  {item.answer ? (
                                    <div className="text-sm text-gray-800 leading-relaxed">
                                      <MessageRenderer
                                        content={item.answer}
                                        messageId={`live-qa-${idx}`}
                                        className=""
                                        citationSources={item.citations || []}
                                      />
                                      {isActive && (
                                        <span className="inline-block w-2 h-4 bg-indigo-600 animate-pulse ml-1 rounded" />
                                      )}
                                    </div>
                                  ) : (
                                    <div className="text-sm text-gray-500">
                                      {isActive ? (
                                        <div className="loading-dots">
                                          <span></span>
                                          <span></span>
                                          <span></span>
                                        </div>
                                      ) : (
                                        <span className="italic">Waiting...</span>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Citations */}
                                  {item.citations && item.citations.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-gray-100 animate-fadeIn">
                                      <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                                        <FontAwesomeIcon icon={faQuoteLeft} className="mr-1 text-gray-400" />
                                        Sources
                                      </h4>
                                      <div className="space-y-1">
                                        {item.citations.map((c: any, i: number) => (
                                          <div key={i} className="text-xs text-gray-600 flex items-start hover:bg-gray-50 p-1 rounded transition-colors">
                                            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold mr-2 flex-shrink-0">
                                              {i + 1}
                                            </span>
                                            <span className="flex-1">
                                              {c.title || 'Source'}
                                              {c.url && (
                                                <a 
                                                  href={c.url} 
                                                  target="_blank" 
                                                  rel="noreferrer" 
                                                  className="ml-1 text-blue-600 hover:text-blue-800 inline-flex items-center"
                                                >
                                                  <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs" />
                                                </a>
                                              )}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Progress indicator */}
                  {isLiveRunActive && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Progress</span>
                        <span className="text-gray-900 font-medium">
                          {liveRunItems.filter(item => item.status === 'done').length} / {liveRunItems.length} completed
                        </span>
                      </div>
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(liveRunItems.filter(item => item.status === 'done').length / liveRunItems.length) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
              /* Session View - Show when not in live run */
              selectedSession ? (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        Session Questions
                      </h2>
                      <p className="text-sm text-gray-500">
                        {selectedSession.qa_pairs.length} questions generated from selected sources
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {canManageQA && !selectedSession.qa_pairs.some(q => q.answer) && (
                        <button
                          onClick={startLiveRun}
                          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm flex items-center"
                        >
                          <FontAwesomeIcon icon={faStream} className="mr-2" />
                          Generate Answers
                        </button>
                      )}
                      <button
                        onClick={() => copySessionToClipboard(selectedSession)}
                        className="text-gray-600 hover:text-gray-800 px-3 py-2 rounded text-sm hover:bg-gray-100 flex items-center"
                        title="Copy session to clipboard"
                      >
                        <FontAwesomeIcon icon={faCopy} className="mr-1" />
                        Copy
                      </button>
                    </div>
                  </div>

                  {/* Questions Only View - Show when no answers */}
                  {!selectedSession.qa_pairs.some(q => q.answer) && (
                    <div className="space-y-3">
                      {selectedSession.qa_pairs.map((qaPair, index) => (
                        <div key={index} className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                                <span className="text-xs font-semibold text-blue-600">{index + 1}</span>
                              </div>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-gray-800">{qaPair.question}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Full Q&A Pairs - Show when answers exist */}
                  {selectedSession.qa_pairs.some(q => q.answer) && (
                    <div className="space-y-6">
                      {selectedSession.qa_pairs.map((qaPair, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                          {/* Question */}
                          <div className="bg-gray-50 p-4 border-b">
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                  <FontAwesomeIcon icon={faUser} className="text-blue-600 text-sm" />
                                </div>
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <h3 className="font-medium text-gray-900 mb-1">Question {index + 1}</h3>
                                    {/* Small feedback indicator on question header */}
                                    {qaPair.feedback && (
                                      <span className={`ml-2 text-xs ${
                                        qaPair.feedback.type === 'thumbs_up' ? 'text-green-600' :
                                        qaPair.feedback.type === 'thumbs_down' ? 'text-red-600' :
                                        'text-yellow-600'
                                      }`}>
                                        {qaPair.feedback.type === 'thumbs_up' ? '👍' :
                                         qaPair.feedback.type === 'thumbs_down' ? '👎' :
                                         '⚠️'}
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => toggleQuestionExpansion(`${selectedSession.id}-${index}`)}
                                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                  >
                                    <FontAwesomeIcon 
                                      icon={expandedQuestions[`${selectedSession.id}-${index}`] ? faEye : faQuestionCircle} 
                                      className="mr-1" 
                                    />
                                    {expandedQuestions[`${selectedSession.id}-${index}`] ? 'Hide' : 'Show'} Answer
                                  </button>
                                </div>
                                <p className="text-gray-700">{qaPair.question}</p>
                              </div>
                            </div>
                          </div>

                          {/* Answer */}
                          {expandedQuestions[`${selectedSession.id}-${index}`] && (
                          <div className="p-4">
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                  <FontAwesomeIcon icon={faRobot} className="text-green-600 text-sm" />
                                </div>
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="font-medium text-gray-900">Answer</h4>
                                </div>
                                
                                {/* Prominent Feedback Reason Display at Top */}
                                {qaPair.feedback && qaPair.feedback.type === 'thumbs_down' && qaPair.feedback.reason && (
                                  <div className="feedback-reason">
                                    <div className="feedback-reason-header">
                                      <span className="feedback-icon">👎</span>
                                      <span className="feedback-label">Negative Feedback</span>
                                    </div>
                                    <div className="feedback-reason-content">
                                      {qaPair.feedback.reason}
                                    </div>
                                    {qaPair.feedback.additional_comments && (
                                      <div className="feedback-comments">
                                        <strong>Additional Comments:</strong> {qaPair.feedback.additional_comments}
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Use MessageRenderer for consistent citation support */}
                                <MessageRenderer
                                  content={qaPair.answer}
                                  messageId={`qa-${selectedSession.id}-${index}`}
                                  className=""
                                  citationSources={Object.entries(qaPair.sources || {}).map(([key, source]: [string, any]) => ({
                                    title: source.title || `Source ${key.replace('source_', '')}`,
                                    url: source.url || '',
                                    text: source.content || ''
                                  }))}
                                />

                                {/* Small feedback indicator at bottom for reference */}
                                {qaPair.feedback && (
                                  <div className="flex items-center space-x-2 text-xs mt-3 pt-3 border-t border-gray-100">
                                    <span className={`px-2 py-1 rounded ${
                                      qaPair.feedback.type === 'thumbs_up' ? 'bg-green-100 text-green-700' :
                                      qaPair.feedback.type === 'thumbs_down' ? 'bg-red-100 text-red-700' :
                                      'bg-yellow-100 text-yellow-700'
                                    }`}>
                                      {qaPair.feedback.type === 'thumbs_up' ? '👍 Positive' :
                                       qaPair.feedback.type === 'thumbs_down' ? '👎 Negative' :
                                       '⚠️ Needs Review'}
                                    </span>
                                    <span className="text-gray-500 text-xs">
                                      Feedback given on {new Date(qaPair.feedback.created_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                )}

                                {/* Feedback Buttons for each Q&A pair */}
                                <div className="mt-4 pt-3 border-t border-gray-100">
                                  <FeedbackButtons
                                    messageId={`qa-${selectedSession.id}-${index}`}
                                    onFeedback={handleQAFeedback}
                                    isWidgetMode={false}
                                    isAuthenticated={!!localStorage.getItem('auth_token')}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
                  <FontAwesomeIcon icon={faEye} className="text-gray-400 text-4xl mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Session Selected</h3>
                  <p className="text-gray-600">Select a QA session from the left to view the questions and answers.</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Citation Tooltip */}
      <CitationTooltip
        visible={citationTooltip.visible}
        title={citationTooltip.title}
        url={citationTooltip.url}
        content={citationTooltip.content}
        x={citationTooltip.x}
        y={citationTooltip.y}
        position={citationTooltip.position}
        horizontalPosition={citationTooltip.horizontalPosition}
        sourceType={citationTooltip.sourceType}
        theme="light"
        onMouseEnter={tooltipEventHandlers.onMouseEnter}
        onMouseLeave={tooltipEventHandlers.onMouseLeave}
      />
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setShowDeleteModal(false); setSessionToDelete(null); }} />
          <div className="relative bg-white rounded-lg shadow-xl border w-full max-w-md mx-4">
            <div className="px-5 py-4 border-b flex items-center">
              <div className="w-9 h-9 rounded-full bg-red-100 text-red-600 flex items-center justify-center mr-3">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
              <h3 className="text-base font-semibold text-gray-900">Delete QA session?</h3>
            </div>
            <div className="px-5 py-4 text-sm text-gray-700 space-y-2">
              <p>This will permanently remove the selected QA session and its questions/answers.</p>
              {sessionToDelete && (
                <div className="bg-gray-50 border border-gray-200 rounded p-3 text-xs text-gray-600">
                  <div className="flex items-center justify-between">
                    <span>Questions</span>
                    <span className="font-medium text-gray-900">{sessionToDelete.qa_pairs.length}</span>
                  </div>
                  <div className="mt-1 text-gray-500">{new Date(sessionToDelete.created_at).toLocaleString()}</div>
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-end gap-2">
              <button
                className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                onClick={() => { setShowDeleteModal(false); setSessionToDelete(null); }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700"
                onClick={hardDeleteSelectedSession}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default QAValidation;